import React, { useState, useEffect, useMemo, useCallback } from "react";
import { usePaymentTypes } from "../../hooks/usePaymentTypes";
import { useTranslation } from "react-i18next";

const SelectPaymentModal = ({ isOpen, onClose, onSave, isLoading = false, title, paymentNotes = "", paymentType = "", price = 0, currency = "USD", allowAmount = false, totalAmount = null, dueAmount = null }) => {
	const { t, i18n } = useTranslation();
	const locale = i18n.language === "ar" ? "ar-SA" : "en-US";
	const { paymentTypes, loadingPaymentTypes } = usePaymentTypes();
	const [selectedMethod, setSelectedMethod] = useState(paymentType);
	const [notes, setNotes] = useState(paymentNotes);
	const [amount, setAmount] = useState(price);
	useEffect(() => {
		if (paymentNotes !== null && paymentNotes !== undefined) {
			setNotes(paymentNotes);
		}
	}, [paymentNotes]);

	// Keep the editable amount in sync with the due amount while the modal is open
	useEffect(() => {
		if (allowAmount) {
			setAmount(price);
		}
	}, [allowAmount, price, isOpen]);

	const amountValue = parseFloat(amount);
	const amountValid = !Number.isNaN(amountValue) && amountValue > 0
		&& amountValue <= (parseFloat(dueAmount !== null ? dueAmount : price) || 0) + 0.001;

	const getIconForPayment = useMemo(() => {
		return (label) => {
			const l = (label || "").toLowerCase();
			if (l.includes("credit") || l.includes("debit") || l.includes("card")) {
				return (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect x="2" y="5" width="20" height="14" rx="2" />
						<path d="M2 10h20" />
					</svg>
				);
			}
			if (l.includes("cash")) {
				return (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect x="2" y="6" width="20" height="12" rx="2" />
						<circle cx="12" cy="12" r="2" />
					</svg>
				);
			}
			if (l.includes("bank")) {
				return (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M3 10h18" />
						<path d="M5 10v8" />
						<path d="M19 10v8" />
						<path d="M2 18h20" />
						<path d="M12 4l10 6H2l10-6z" />
					</svg>
				);
			}
			if (l.includes("mobile") || l.includes("wallet")) {
				return (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect x="7" y="2" width="10" height="20" rx="2" />
						<circle cx="12" cy="18" r="1" />
					</svg>
				);
			}
			if (l.includes("check") || l.includes("cheque")) {
				return (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect x="3" y="6" width="18" height="12" rx="2" />
						<path d="M7 12h10" />
					</svg>
				);
			}
			return (
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<circle cx="12" cy="12" r="9" />
					<path d="M12 7v5l3 3" />
				</svg>
			);
		};
	}, []);

	const getPaymentTypeColor = (label) => {
		const l = (label || "").toLowerCase();
		if (l.includes("credit") || l.includes("debit") || l.includes("card")) {
			return { gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "#3b82f6" };
		}
		if (l.includes("cash")) {
			return { gradient: "linear-gradient(135deg, #059669 0%, #047857 100%)", color: "#059669" };
		}
		if (l.includes("bank")) {
			return { gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", color: "#6366f1" };
		}
		if (l.includes("mobile") || l.includes("wallet")) {
			return { gradient: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)", color: "#dc2626" };
		}
		if (l.includes("check") || l.includes("cheque")) {
			return { gradient: "linear-gradient(135deg, #d97706 0%, #b45309 100%)", color: "#d97706" };
		}
		return { gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)", color: "#64748b" };
	};

	useEffect(() => {
		if (!isOpen) {
			// Reset to prop values when modal closes, or empty strings if props are null/undefined
			setSelectedMethod(paymentType);
			setNotes(paymentNotes);
		}
	}, [isOpen, paymentType, paymentNotes]);

	const formatMoney = useCallback(
		(value) => {
			const number = Number(value);
			if (Number.isNaN(number)) return "0.00";
			return number.toLocaleString(locale, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},
		[locale]
	);

	const modalTitle = title || t("receptionOutpatients.edit.payment.title");
	const priceLabel = t("receptionOutpatients.edit.payment.price", { amount: formatMoney(price), currency });
	const selectedPaymentInfo = useMemo(
		() => paymentTypes.find((pt) => pt.value?.toString() === selectedMethod?.toString()),
		[paymentTypes, selectedMethod]
	);
	const selectedPaymentLabel = selectedPaymentInfo?.label || t("receptionOutpatients.edit.payment.summary.unknown");
	const saveButtonLabel = isLoading
		? t("receptionOutpatients.edit.payment.buttons.saving")
		: t("receptionOutpatients.edit.payment.buttons.save");

	if (!isOpen) return null;

	return (
		<div
			className="payment-modal-overlay"
			dir={i18n.dir()}
			lang={i18n.language}
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				background: "rgba(0, 0, 0, 0.6)",
				backdropFilter: "blur(12px)",
				display: "grid",
				placeItems: "center",
				zIndex: 10000,
				padding: "20px",
				animation: "fadeIn 0.4s ease-out"
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget && !isLoading) {
					onClose && onClose();
				}
			}}
		>
			<div
				className="payment-modal-container"
				style={{
					background: "rgba(255, 255, 255, 0.95)",
					backdropFilter: "blur(20px)",
					borderRadius: 16,
					width: "min(520px, 95vw)",
					maxWidth: 520,
					padding: 0,
					boxShadow: "0 40px 80px -20px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.2), inset 0 1px 0 rgba(255,255,255,0.6)",
					border: "1px solid rgba(255,255,255,0.3)",
					animation: "scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
					position: "relative",
					overflow: "hidden"
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div
					className="payment-modal-header"
					style={{
						padding: "20px 24px 16px",
						background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
						borderBottom: "1px solid #e2e8f0",
						position: "relative",
						overflow: "hidden"
					}}
				>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
							<div style={{
								width: 44,
								height: 44,
								borderRadius: 12,
								background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
								display: "grid",
								placeItems: "center",
								boxShadow: "0 6px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)"
							}}>
								<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
									<rect x="2" y="5" width="20" height="14" rx="2" />
									<path d="M2 10h20" />
								</svg>
							</div>
							<div>
								<div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", lineHeight: 1.2, marginBottom: 2 }}>{modalTitle}</div>
								<div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
									{t("receptionOutpatients.edit.payment.subtitle")}
								</div>
							</div>
						</div>

						{/* Price Badge */}
						<div style={{
							background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
							borderRadius: 12,
							padding: "8px 16px",
							boxShadow: "0 6px 24px rgba(5, 150, 105, 0.3)",
							display: "flex",
							alignItems: "center",
							gap: 6,
							border: "1px solid rgba(5, 150, 105, 0.2)"
						}}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
								<circle cx="12" cy="12" r="10" />
								<path d="M12 6v6l4 2" />
							</svg>
							<div style={{ fontSize: 16, color: "white", fontWeight: 700 }}>{priceLabel}</div>
						</div>

					</div>
				</div>

				{/* Body */}
				<div style={{ padding: "20px" }}>
					<div className="payment-grid" style={{ gap: 14, alignItems: "stretch" }}>
						{loadingPaymentTypes ? (
							<div style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 12,
								color: "#6B7280",
								fontSize: 16,
								gridColumn: "1 / -1",
								padding: "40px 20px",
								background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
								borderRadius: 16,
								border: "1px solid #E2E8F0"
							}}>
								<span style={{
									width: 20,
									height: 20,
									border: "3px solid #3B82F6",
									borderTop: "3px solid transparent",
									borderRadius: "50%",
									display: "inline-block",
									animation: "spin 1s linear infinite"
								}} />
								{t("receptionOutpatients.edit.payment.loading")}
							</div>
						) : (
							paymentTypes.map((pt) => {
								const isSelected = selectedMethod.toString() === pt.value.toString();
								const paymentColors = getPaymentTypeColor(pt.label);
								return (
									<button
										key={pt.value}
										type="button"
										className={`payment-method-card ${isSelected ? 'selected' : ''}`}
										onClick={() => !isLoading && setSelectedMethod(pt.value)}
										style={{
											width: "100%",
											height: "100%",
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											gap: 12,
											padding: "18px 16px",
											borderRadius: 16,
											border: isSelected ? `2px solid ${paymentColors.color}` : "2px solid #E5E7EB",
											background: isSelected
												? `linear-gradient(135deg, ${paymentColors.color}15 0%, ${paymentColors.color}08 100%)`
												: "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)",
											cursor: isLoading ? "not-allowed" : "pointer",
											transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
											outline: "none",
											minHeight: 110,
											position: "relative",
											overflow: "hidden",
											boxShadow: isSelected
												? `0 16px 32px -8px ${paymentColors.color}40, 0 0 0 1px ${paymentColors.color}20`
												: "0 4px 12px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.05)",
											transform: isSelected ? "translateY(-2px)" : "translateY(0)",
											opacity: isLoading ? 0.6 : 1
										}}
										onMouseEnter={(e) => {
											if (!isSelected && !isLoading) {
												e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
												e.currentTarget.style.transform = "translateY(-2px)";
												e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.1)";
											}
										}}
										onMouseLeave={(e) => {
											if (!isSelected && !isLoading) {
												e.currentTarget.style.background = "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)";
												e.currentTarget.style.transform = "translateY(0)";
												e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.05)";
											}
										}}
									>
										{/* Selection indicator */}
										{isSelected && (
											<div style={{
												position: "absolute",
												top: 8,
												right: 8,
												width: 20,
												height: 20,
												borderRadius: "50%",
												background: paymentColors.gradient,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												animation: "pulse 2s infinite",
												boxShadow: `0 3px 8px ${paymentColors.color}40`
											}}>
												<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
													<path d="M20 6L9 17l-5-5" />
												</svg>
											</div>
										)}

										{/* Icon container */}
										<div style={{
											width: 48,
											height: 48,
											borderRadius: 16,
											background: isSelected ? paymentColors.gradient : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
											display: "grid",
											placeItems: "center",
											boxShadow: isSelected
												? `0 6px 18px ${paymentColors.color}30, inset 0 1px 0 rgba(255,255,255,0.2)`
												: "0 3px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)",
											transition: "all 0.3s ease",
											position: "relative",
											overflow: "hidden"
										}}>
											{/* Shine effect */}
											<div style={{
												position: "absolute",
												top: "-50%",
												left: "-50%",
												width: "200%",
												height: "200%",
												background: "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)",
												transform: "rotate(-45deg)",
												transition: "all 0.6s ease",
												opacity: 0
											}}
												onMouseEnter={(e) => { e.target.style.opacity = 1; e.target.style.transform = "rotate(-45deg) translateX(100%)"; }}
											/>

											<div style={{
												color: isSelected ? "white" : paymentColors.color,
												transition: "all 0.3s ease",
												transform: isSelected ? "scale(1.05)" : "scale(1)"
											}}>
												<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
													{getIconForPayment(pt.label).props.children}
												</svg>
											</div>
										</div>

										{/* Label and badge */}
										<div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }}>
											<div style={{
												fontSize: 14,
												fontWeight: 600,
												color: isSelected ? paymentColors.color : "#1e293b",
												transition: "all 0.3s ease"
											}}>
												{pt.label}
											</div>

											{/* Payment type badge */}
											<div style={{
												fontSize: 10,
												fontWeight: 500,
												color: isSelected ? paymentColors.color : "#64748b",
												background: isSelected
													? `${paymentColors.color}15`
													: "#f1f5f9",
												padding: "3px 6px",
												borderRadius: 6,
												border: isSelected
													? `1px solid ${paymentColors.color}30`
													: "1px solid #e2e8f0",
												transition: "all 0.3s ease"
											}}>
												{pt.type_display || pt.type || t("receptionOutpatients.edit.payment.badges.fallback")}
											</div>
										</div>

										<input
											type="radio"
											name="paymentMethod"
											value={pt.value}
											checked={isSelected}
											onChange={(e) => setSelectedMethod(e.target.value)}
											style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
										/>
									</button>
								);
							})
						)}
					</div>

					{/* Paid amount — only when the caller allows partial amounts (visit close) */}
					{allowAmount && (
						<div style={{ marginTop: 20 }}>
							{/* Visit billing context: total billed, what's already been
							    paid (cash + discounts), and what's still due */}
							{totalAmount !== null && (
								<div style={{
									display: "flex",
									gap: 10,
									marginBottom: 12
								}}>
									<div style={{
										flex: 1,
										background: "rgba(12, 74, 110, 0.06)",
										border: "1px solid rgba(12, 74, 110, 0.2)",
										borderRadius: 10,
										padding: "10px 12px"
									}}>
										<div style={{ fontSize: 11, fontWeight: 600, color: "#0C4A6E", textTransform: "uppercase", letterSpacing: "0.5px" }}>
											{t('receptionOutpatients.close.paymentModal.totalAmount', 'Total amount')}
										</div>
										<div style={{ fontSize: 16, fontWeight: 800, color: "#0C4A6E", marginTop: 2 }}>
											{currency} {formatMoney(totalAmount)}
										</div>
									</div>
									<div style={{
										flex: 1,
										background: "rgba(5, 150, 105, 0.07)",
										border: "1px solid rgba(5, 150, 105, 0.25)",
										borderRadius: 10,
										padding: "10px 12px"
									}}>
										<div style={{ fontSize: 11, fontWeight: 600, color: "#047857", textTransform: "uppercase", letterSpacing: "0.5px" }}>
											{t('receptionOutpatients.close.paymentModal.paidSoFar', 'Paid so far')}
										</div>
										<div style={{ fontSize: 16, fontWeight: 800, color: "#047857", marginTop: 2 }}>
											{currency} {formatMoney((parseFloat(totalAmount) || 0) - (parseFloat(dueAmount !== null ? dueAmount : price) || 0))}
										</div>
									</div>
									<div style={{
										flex: 1,
										background: "rgba(239, 68, 68, 0.06)",
										border: "1px solid rgba(239, 68, 68, 0.25)",
										borderRadius: 10,
										padding: "10px 12px"
									}}>
										<div style={{ fontSize: 11, fontWeight: 600, color: "#DC2626", textTransform: "uppercase", letterSpacing: "0.5px" }}>
											{t('receptionOutpatients.close.paymentModal.dueAmount', 'Due amount')}
										</div>
										<div style={{ fontSize: 16, fontWeight: 800, color: "#DC2626", marginTop: 2 }}>
											{currency} {formatMoney(dueAmount !== null ? dueAmount : price)}
										</div>
									</div>
								</div>
							)}
							<label style={{
								display: "flex",
								fontSize: 14,
								fontWeight: 600,
								color: "#1e293b",
								marginBottom: 8,
								alignItems: "center",
								gap: 6
							}}>
								{t('receptionOutpatients.close.paymentModal.amountLabel', 'Amount paid')}
							</label>
							<input
								type="number"
								min="0"
								step="0.01"
								value={amount}
								onChange={(e) => setAmount(e.target.value)}
								disabled={isLoading}
								style={{
									width: "100%",
									padding: 12,
									borderRadius: 12,
									border: `2px solid ${amountValid ? '#e2e8f0' : '#ef4444'}`,
									background: "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)",
									fontSize: 15,
									fontWeight: 700,
									color: "#1e293b",
									outline: "none",
									fontFamily: "inherit",
									boxSizing: "border-box",
								}}
							/>
							{!amountValid && (
								<div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>
									{t('receptionOutpatients.close.paymentModal.amountInvalid', 'Enter an amount greater than 0')}
								</div>
							)}
						</div>
					)}

					{/* Notes Section */}
					<div style={{ marginTop: 20 }}>
						<label style={{
							fontSize: 14,
							fontWeight: 600,
							color: "#1e293b",
							marginBottom: 8,
							display: "flex",
							alignItems: "center",
							gap: 6
						}}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
								<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
								<polyline points="14,2 14,8 20,8" />
								<line x1="16" y1="13" x2="8" y2="13" />
								<line x1="16" y1="17" x2="8" y2="17" />
								<polyline points="10,9 9,9 8,9" />
							</svg>
							{t("receptionOutpatients.edit.payment.notes.label")}
						</label>
						<textarea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder={t("receptionOutpatients.edit.payment.notes.placeholder")}
							disabled={isLoading}
							style={{
								width: "100%",
								minHeight: 70,
								padding: 12,
								borderRadius: 12,
								border: "2px solid #e2e8f0",
								background: "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)",
								fontSize: 13,
								color: "#1e293b",
								resize: "vertical",
								outline: "none",
								transition: "all 0.3s ease",
								opacity: isLoading ? 0.6 : 1,
								cursor: isLoading ? "not-allowed" : "text",
								fontFamily: "inherit",
								boxShadow: "0 3px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)"
							}}
							onFocus={(e) => {
								e.target.style.borderColor = "#3b82f6";
								e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255,255,255,0.5)";
								e.target.style.transform = "translateY(-1px)";
							}}
							onBlur={(e) => {
								e.target.style.borderColor = "#e2e8f0";
								e.target.style.boxShadow = "0 3px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)";
								e.target.style.transform = "translateY(0)";
							}}
						/>
					</div>
				</div>

				{/* Footer */}
				<div style={{
					padding: "16px 20px",
					borderTop: "1px solid #e2e8f0",
					background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between"
				}}>
					{/* Payment Info */}
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{selectedMethod && (
							<div style={{
								width: 32,
								height: 32,
								borderRadius: 8,
								background: getPaymentTypeColor(selectedPaymentInfo?.label || "").gradient,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								boxShadow: "0 3px 8px rgba(0,0,0,0.1)"
							}}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
									{getIconForPayment(selectedPaymentInfo?.label || "").props.children}
								</svg>
							</div>
						)}
						<div>
							<div style={{ fontSize: 12, color: "#64748b", marginBottom: 1 }}>
								{selectedMethod
									? selectedPaymentLabel
									: t("receptionOutpatients.edit.payment.summary.none")
								}
							</div>
							<div style={{ fontSize: 16, fontWeight: 700, color: "#059669" }}>{priceLabel}</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div style={{ display: "flex", gap: 12 }}>
						<button
							onClick={() => onClose && onClose()}
							className="payment-modal-cancel-btn"
							style={{
								padding: "10px 18px",
								borderRadius: 10,
								border: "2px solid #e2e8f0",
								background: "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)",
								cursor: isLoading ? "not-allowed" : "pointer",
								opacity: isLoading ? 0.6 : 1,
								fontSize: 13,
								fontWeight: 600,
								color: "#64748b",
								transition: "all 0.3s ease",
								display: "flex",
								alignItems: "center",
								gap: 6
							}}
							disabled={isLoading}
							onMouseEnter={(e) => { if (!isLoading) { e.target.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)"; e.target.style.borderColor = "#cbd5e1"; e.target.style.transform = "translateY(-1px)"; } }}
							onMouseLeave={(e) => { if (!isLoading) { e.target.style.background = "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)"; e.target.style.borderColor = "#e2e8f0"; e.target.style.transform = "translateY(0)"; } }}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<line x1="18" y1="6" x2="6" y2="18" />
								<line x1="6" y1="6" x2="18" y2="18" />
							</svg>
							{t("common.cancel")}
						</button>
						<button
							onClick={() => onSave && onSave({ method: selectedMethod, notes: notes.trim(), amount: allowAmount ? parseFloat(amount) : undefined })}
							className="payment-modal-save-btn"
							style={{
								padding: "10px 20px",
								borderRadius: 10,
								border: 0,
								background: selectedMethod && !isLoading
									? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
									: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
								color: "#FFFFFF",
								cursor: selectedMethod && !isLoading ? "pointer" : "not-allowed",
								display: "flex",
								alignItems: "center",
								gap: 8,
								boxShadow: selectedMethod && !isLoading
									? "0 8px 24px rgba(37,99,235,0.4), 0 0 0 1px rgba(37,99,235,0.2)"
									: "0 3px 8px rgba(0,0,0,0.1)",
								fontSize: 13,
								fontWeight: 600,
								transition: "all 0.3s ease",
								opacity: isLoading ? 0.8 : 1
							}}
							disabled={!selectedMethod || isLoading || (allowAmount && !amountValid)}
							onMouseEnter={(e) => {
								if (selectedMethod && !isLoading) {
									e.target.style.transform = "translateY(-1px)";
									e.target.style.boxShadow = "0 12px 32px rgba(37,99,235,0.5), 0 0 0 1px rgba(37,99,235,0.3)";
								}
							}}
							onMouseLeave={(e) => {
								if (selectedMethod && !isLoading) {
									e.target.style.transform = "translateY(0)";
									e.target.style.boxShadow = "0 8px 24px rgba(37,99,235,0.4), 0 0 0 1px rgba(37,99,235,0.2)";
								}
							}}
						>
							{isLoading && (
								<span style={{
									width: 14,
									height: 14,
									border: "2px solid #FFFFFF",
									borderTop: "2px solid transparent",
									borderRadius: "50%",
									display: "inline-block",
									animation: "spin 1s linear infinite"
								}} />
							)}
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M20 6L9 17l-5-5" />
							</svg>
							{saveButtonLabel}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SelectPaymentModal;


